import { FC, ReactNode, useContext } from "react";
import { Box, Flex, Text, Space } from "@mantine/core";
import { motion } from "framer-motion";
import { useMediaQuery } from "@mantine/hooks";
import MessageBarContext from '@/utils/contexts/sc-message-bar-context';
import constants from "@/utils/constants";

const BLUE_START = '#003ece';
const BLUE_MID_1 = '#0138b9';
const BLUE_MID_2 = '#04287e';
const BLUE_END = '#001E7A';
const BLUE_GRADIENT = `radial-gradient(100% 115% at 0% 100%, ${BLUE_START} 0%, ${BLUE_MID_1} 45%, ${BLUE_MID_2} 80%, ${BLUE_END} 100%)`;
const BLUE_GRADIENT_ALT = `radial-gradient(115% 130% at 15% 90%, ${BLUE_END} 0%, ${BLUE_MID_2} 45%, ${BLUE_MID_1} 80%, ${BLUE_START} 100%)`;

// Blue gradient panel background + grid lines only — no content. Kept separate from
// RightPanelContent so the color can stretch full-height while the content is positioned
// by the shared content layer below (see AuthSplitLayout).
const RightPanelBackground: FC = () => (
    <Box
        component={motion.div}
        display={{ base: 'none', md: 'flex' }}
        animate={{ background: [BLUE_GRADIENT, BLUE_GRADIENT_ALT] }}
        transition={{ duration: 15, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
        style={{ flex: 1, position: 'relative', overflow: 'hidden', background: BLUE_GRADIENT }}
    >
        <Box
            style={{
                position: 'absolute',
                inset: 0,
                backgroundImage:
                    'repeating-linear-gradient(to right,  rgba(255,255,255,0.06) 0 1px, transparent 1px 48px),' +
                    'repeating-linear-gradient(to bottom, rgba(255,255,255,0.06) 0 1px, transparent 1px 48px)',
            }}
        />
    </Box>
);

const RightPanelContent: FC = () => {
    const shortHeight = useMediaQuery('(max-height: 768px)');
    return (
        <Flex
            direction={'column'}
            align={'center'}
            display={{ base: 'none', md: 'flex' }}
            style={{ flex: 1, minWidth: 0 }}
            px={48}
        >
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
            >
                <Text
                    ta={'center'}
                    c={'#FFFFFF'}
                    fw={700}
                    maw={613}
                    lh={'normal'}
                    style={{ fontSize: shortHeight ? 24 : 32 }}
                >
                    Manage your team, jobs, and customers <span style={{ backgroundColor: '#51B27E', padding: '0 8px', borderRadius: '4px', display: 'inline-block' }}>all from one place.</span>
                </Text>
            </motion.div>

            <Box mt={shortHeight ? 40 : 60} maw={650}>
                <img src="/login-page/scLoginControls.png" alt="ServCraft Controls" style={{ maxWidth: '100%', height: 'auto' }} />
            </Box>
        </Flex>
    );
};

interface AuthSplitLayoutProps {
    children: ReactNode;
    /** Max width (px) of the visible content column. Also drives the logo's horizontal alignment
     *  so it lines up with the content's left edge. Defaults to 610 (legacy width). */
    contentWidth?: number;
}

// Column width (px) at which the logo stops tracking the centered content and pins to a fixed
// left margin instead — this is what makes it "yield to the left" on very large screens.
const LOGO_YIELD_TRIGGER_WIDTH = 960;

const AuthSplitLayout: FC<AuthSplitLayoutProps> = ({ children, contentWidth = 610 }) => {
    const { isActive } = useContext(MessageBarContext);
    const halfContentWidth = contentWidth / 2;
    const logoMaxOffset = LOGO_YIELD_TRIGGER_WIDTH / 2 - halfContentWidth;

    return (
        <Flex direction="column" mih={'100vh'} align={'stretch'}>
            {isActive && <Space h={48} />}
            {/* This wrapper is normal flow (not absolutely positioned), so if the content ever
                needs more vertical room than the viewport has, it grows the page and the browser
                scrolls — instead of the content silently overflowing and overlapping the logo. */}
            <Flex direction={'column'} style={{ flex: 1, position: 'relative' }}>

                {/* Full-height background columns — white left, blue gradient right. Purely
                    decorative, so it's safe for this to be the one absolutely positioned piece:
                    it just stretches to match whatever height the content ends up needing. */}
                <Flex style={{ position: 'absolute', inset: 0, zIndex: 0 }} align={'stretch'}>
                    <Box style={{ flex: 1, minWidth: 0, backgroundColor: '#FFFFFF' }} />
                    <RightPanelBackground />
                </Flex>

                {/* Mobile header bar — pl tracks the same "centered content" math as the content
                    box below (max(24px, half of the leftover space)), so the icon lines up with
                    the centered content instead of sitting flush left while content is centered. */}
                <Box
                    display={{ base: 'flex', md: 'none' }}
                    style={{ position: 'relative', zIndex: 1, backgroundColor: BLUE_START, alignItems: 'center' }}
                    h={64}
                    pl={`max(24px, calc(50% - ${halfContentWidth}px))`}
                >
                    <img src="/logo-type-white.svg" alt="ServCraft" height={32} style={{ width: 'auto', transform: 'translateX(-15px)' }} />
                </Box>

                {/* Logo top-left (Desktop) — width-matched to the left column (via the flex:1
                    spacer below) so its `50%` resolves against the column's width, not the full
                    page width; otherwise it drifts toward the right column as the screen narrows.
                    Sits in normal flow so it always reserves its own space above the content,
                    rather than floating over it on short viewports. */}
                <Flex display={{ base: 'none', md: 'flex' }} style={{ position: 'relative', zIndex: 1 }}>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                        <Box pt={28} pl={`clamp(24px, calc(50% - ${halfContentWidth}px), ${logoMaxOffset}px)`}>
                            <img src="/logo-type-blue.svg" alt="ServCraft" height={40} style={{ width: "auto", transform: 'translateX(-15px)' }} />
                        </Box>
                    </Box>
                    <Box style={{ flex: 1, minWidth: 0 }} />
                </Flex>

                {/* Shared content — both sides start at the same top position, and the pair is
                    centered together as a single group, nudged up ~50px from dead-center.
                    Split between a real margin and a transform rather than one or the other:
                    with align-items:center, a one-sided margin only turns into HALF its size as
                    visible shift (the rest gets redistributed to the other side), so reserving
                    the full 50px via margin alone would demand 100px of extra page height —
                    enough to force an unwanted scrollbar on taller forms at ordinary laptop
                    viewport heights. A pure transform costs nothing but is paint-only, so it
                    ignores the "grow the page instead of overlapping the logo" behavior below
                    and can crash into the logo on short viewports. Splitting it — 25px of real
                    margin (→ 25px of protected shift) plus a 25px transform for the rest — keeps
                    the overlap gap safely positive without paying the full 100px height cost. */}
                <Flex align={'center'} justify={'center'} style={{ flex: 1, position: 'relative', zIndex: 1 }} py={24}>
                    <Flex align={'flex-start'} w={'100%'} mb={50} style={{ transform: 'translateY(-25px)' }}>
                        <Flex justify={'center'} style={{ flex: 1, minWidth: 0 }} px={24}>
                            <Box w={'100%'} maw={contentWidth}>
                                {children}
                            </Box>
                        </Flex>

                        <RightPanelContent />
                    </Flex>
                </Flex>
            </Flex>

            <div style={{ position: "fixed", bottom: 12, left: 16, fontSize: "0.6rem", color: "#6B6F76" }}>{constants.appVersion()}</div>
        </Flex>
    );
};

export default AuthSplitLayout;
